<template>
    <section
        id="stats"
        class="stats"
    >

        <div class="container">

            <div class="stats__header">

                <span class="stats__eyebrow">
                    {{ 'home_stats_header' | staticContentAlt }}
                </span>

                <h2>
                    {{ 'home_stats_header' | staticTitle }}
                </h2>

                <p>
                    {{ 'home_stats_header' | staticContent }}
                </p>

            </div>

            <div
                ref="statsGrid"
                class="stats__grid"
            >

                <article
                    v-for="(stat, index) in stats"
                    :key="stat.hash"
                    class="stat-card"
                >

                    <span class="stat-card__index">
                        0{{ index + 1 }}
                    </span>

                    <strong>
                        {{ displayedValues[index] }}%
                    </strong>

                    <span class="stat-card__label">
                        {{ stat.hash | staticTitle }}
                    </span>

                </article>

            </div>

        </div>

    </section>
</template>

<script>
export default {
    name: 'HomeStats',

    data() {
        return {
            hasAnimated: false,
            observer: null,
            animationFrame: null,

            displayedValues: [
                0,
                0,
                0,
                0
            ],

            stats: [
                {
                    value: 100,
                    hash: 'home_stats_1'
                },
                {
                    value: 100,
                    hash: 'home_stats_2'
                },
                {
                    value: 100,
                    hash: 'home_stats_3'
                },
                {
                    value: 100,
                    hash: 'home_stats_4'
                }
            ]
        };
    },

    mounted() {
        this.$nextTick(() => {
            this.createObserver();
        });
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }

        if (this.animationFrame) {
            cancelAnimationFrame(
                this.animationFrame
            );
        }
    },

    methods: {
        createObserver() {
            const target =
                this.$refs.statsGrid;

            if (!target) {
                return;
            }

            if (
                !('IntersectionObserver' in window)
            ) {
                this.startCounters();
                return;
            }

            this.observer =
                new IntersectionObserver(
                    (entries) => {
                        const entry =
                            entries[0];

                        if (
                            entry.isIntersecting &&
                            !this.hasAnimated
                        ) {
                            this.hasAnimated = true;

                            setTimeout(() => {
                                this.startCounters();
                            }, 200);

                            this.observer.disconnect();
                        }
                    },
                    {
                        threshold: 0.2,

                        rootMargin:
                            '0px 0px -25% 0px'
                    }
                );

            this.observer.observe(target);
        },

        startCounters() {
            const duration = 2200;
            const startTime =
                performance.now();

            const animate =
                (currentTime) => {

                    const progress =
                        Math.min(
                            (
                                currentTime -
                                startTime
                            ) /
                            duration,
                            1
                        );

                    const eased =
                        1 -
                        Math.pow(
                            1 - progress,
                            3
                        );

                    const value =
                        Math.round(
                            eased * 100
                        );

                    this.displayedValues.forEach(
                        (item, index) => {
                            this.$set(
                                this.displayedValues,
                                index,
                                value
                            );
                        }
                    );

                    if (progress < 1) {
                        this.animationFrame =
                            requestAnimationFrame(
                                animate
                            );
                    } else {
                        this.displayedValues.forEach(
                            (item, index) => {
                                this.$set(
                                    this.displayedValues,
                                    index,
                                    100
                                );
                            }
                        );
                    }
                };

            this.animationFrame =
                requestAnimationFrame(
                    animate
                );
        }
    }
};
</script>

<style lang="scss" scoped>
.stats {
    position: relative;

    padding:
        5.3125em 1.25em
        5.9375em;

    background:
        radial-gradient(
            circle at 50% 0%,
            rgba(233, 81, 29, .055),
            transparent 30%
        ),
        var(--color-1);

    border-top:
        0.0625em solid
        var(--border-1);

    border-bottom:
        0.0625em solid
        var(--border-1);

    overflow: hidden;
}

.stats::before {
    content: "";

    position: absolute;

    top: -15.625em;
    right: -15.625em;

    width: 31.25em;
    height: 31.25em;

    border:
        0.0625em solid
        rgba(255, 255, 255, .035);

    border-radius: 50%;

    pointer-events: none;
}

.container {
    position: relative;

    width: 100%;
    max-width: var(--container);

    margin: 0 auto;

    z-index: 2;
}

.stats__header {
    max-width: 47.5em;

    margin:
        0 auto
        3.4375em;

    text-align: center;

    h2 {
        margin:
            0 0
            1.125em;

        color: var(--text-1);

        font-size:
            clamp(
                2.125em,
                6vw,
                3.375em
            );

        font-weight: 700;
        line-height: 1;
        -letter-spacing: -0.125em;

        text-transform: uppercase;
    }

    p {
        max-width: 37.5em;

        margin: 0 auto;

        color: var(--text-2);

        font-size: 0.875em;
        line-height: 1.8;
    }
}

.stats__eyebrow {
    display: inline-flex;
    align-items: center;

    gap: 0.75em;

    margin-bottom: 1em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.625em;
    font-weight: 600;
    -letter-spacing: 0.15625em;

    text-transform: uppercase;

    &::before,
    &::after {
        content: "";

        width: 1.5625em;
        height: 0.0625em;

        background: var(--color-3);
    }
}

.stats__grid {
    display: grid;
    grid-template-columns: 1fr;

    gap: 0.0625em;

    background: var(--border-1);

    border:
        0.0625em solid
        var(--border-1);
}

.stat-card {
    position: relative;

    min-height: 13.75em;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding: 2em 1.25em;

    text-align: center;

    background:
        linear-gradient(
            145deg,
            rgba(255, 255, 255, .018),
            rgba(255, 255, 255, .006)
        ),
        var(--color-1);

    overflow: hidden;

    transition:
        background .3s ease,
        transform .3s ease;

    &::after {
        content: "";

        position: absolute;

        left: 50%;
        bottom: 0;

        width: 0;
        height: 0.125em;

        background: var(--color-3);

        transform: translateX(-50%);

        transition:
            width .35s ease;
    }

    &:hover {
        background:
            rgba(255, 255, 255, .025);

        &::after {
            width: 45%;
        }
    }
}

.stat-card__index {
    position: absolute;

    top: 1.125em;
    left: 1.125em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.5625em;
    -letter-spacing: 0.09375em;
}

.stat-card strong {
    display: block;

    margin-bottom: 0.875em;

    color: var(--color-3);

    font-family: var(--font-tech);

    font-size:
        clamp(
            2.75em,
            8vw,
            4.25em
        );

    font-weight: 600;
    line-height: 1;
    -letter-spacing: -0.125em;

    text-shadow:
        0 0 1.375em
        rgba(233, 81, 29, .12);
}

.stat-card__label {
    max-width: 13.125em;

    color: var(--text-1);

    font-size: 0.8125em;
    font-weight: 500;
    line-height: 1.5;
}

@media (min-width: 37.5em) {
    .stats__grid {
        grid-template-columns:
            repeat(2, 1fr);
    }
}

@media (min-width: 56.25em) {
    .stats {
        padding:
            6.5625em 2.5em
            7.1875em;
    }

    .stats__header {
        margin-bottom: 4.0625em;
    }
}

@media (min-width: 68.75em) {
    .stats__grid {
        grid-template-columns:
            repeat(4, 1fr);
    }

    .stat-card {
        min-height: 15.625em;
    }
}
</style>